import React, { useEffect, useState, useRef } from 'react'
import {
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from 'firebase/auth'
import { auth } from '@/utils/firebaseConfig'
import { sendVerificationEmail } from '@/utils/firebaseAuth'
import { useNavigate } from 'react-router-dom'
import {
  getCurrentUser,
  createUser,
  clearUserCache,
  updateUserEmail,
  updateUserFirebaseUid,
} from '@/api/requests/user'
import { createSpace } from '@/api/requests/space'
import { csrfManager } from '@/api/requests'
import { etagManager } from '@/utils/etag-manager'
import { AuthContext } from '@/contexts/auth-context'
import type { UserResponse } from '@/types/api/responses'
import { crossTabAuthSync, type AuthSyncMessage } from '@/utils/cross-tab-auth-sync'
import { logger } from '@/utils/logger'
import { clearTokenCache } from '@/utils/auth-helpers'

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [isSigningUp, setIsSigningUp] = useState(false)
  const navigate = useNavigate()

  const authCheckTimeoutRef = useRef<NodeJS.Timeout>()

  // クロスタブ認証同期の設定
  useEffect(() => {
    const handleCrossTabMessage = (message: AuthSyncMessage) => {
      logger.debug('Received cross-tab auth message:', message)

      switch (message.type) {
        case 'LOGOUT':
          if (user) {
            // 手動ログアウトの場合は即座に同期
            if (message.data?.reason === 'manual_logout') {
              logger.info('Other tab manually logged out, syncing logout state')
              setUser(null)
            } else {
              // それ以外の場合は、Firebase認証状態を確認してから判断
              logger.info('Other tab logout detected, verifying local auth state', {
                reason: message.data?.reason,
                hasFirebaseUser: !!auth.currentUser,
              })

              // Firebase認証状態が本当にログアウトされているか確認
              if (!auth.currentUser) {
                logger.info('Firebase auth confirmed logged out, syncing state')
                setUser(null)
              } else {
                logger.warn('Firebase auth still active, ignoring logout sync', {
                  firebaseUserEmail: auth.currentUser.email,
                })
              }
            }
          }
          break
        case 'AUTH_STATE_CHANGE':
          if (message.data?.hasUser && !user) {
            logger.info('Other tab authenticated, checking local auth state')
            // 他のタブで認証された場合は、ローカルの認証状態を再確認
            // これにより新しいタブでの分析結果表示がスムーズになる
          }
          break
        case 'TOKEN_REFRESH':
          logger.debug('Other tab refreshed token, noting for local requests')
          // トークンリフレッシュの通知は記録のみ
          break
      }
    }

    crossTabAuthSync.onMessage(handleCrossTabMessage)

    return () => {
      // クリーンアップは CrossTabAuthSync で管理
    }
  }, [user])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      // Clear any existing timeout
      if (authCheckTimeoutRef.current) {
        clearTimeout(authCheckTimeoutRef.current)
      }

      // Skip auto-processing during signup to prevent premature navigation
      if (isSigningUp) {
        setLoading(false)
        return
      }

      // クロスタブでの認証状態同期の改善
      // 新しいタブでは即座に処理、既存タブでは長めのデバウンスを使用して競合を回避
      const isInitialLoad = loading
      const debounceTime = isInitialLoad ? 0 : 500 // デバウンス時間を延長して競合を回避

      authCheckTimeoutRef.current = setTimeout(async () => {
        try {
          if (firebaseUser) {
            const email = firebaseUser.email
            if (!email) {
              console.error('Firebase user has no email')
              setUser(null)
              return
            }

            // ✅ SECURITY FIX: Use self-access only via getCurrentUser
            // Check if user exists in our backend using secure self-access
            try {
              const user = await getCurrentUser()
              // 🔄 Migration: Check if Firebase UID is missing and populate it
              if (!user.firebaseUid || user.firebaseUid === null) {
                console.log('🔄 Firebase UID missing, migrating existing user:', {
                  userEmail: user.email,
                  firebaseUid: firebaseUser.uid,
                })

                try {
                  // Ensure CSRF token is ready before Firebase UID update
                  // This prevents race conditions in Cloud Run environments
                  await csrfManager.getToken()

                  // Update user with Firebase UID (migration)
                  const updatedUser = await updateUserFirebaseUid(user.email, firebaseUser.uid)
                  console.log('✅ Firebase UID migration completed successfully')

                  // Clear ETag cache after Firebase UID update to prevent conflicts
                  const userResourceKey = etagManager.getUserResourceKey(user.email)
                  etagManager.clearETag(userResourceKey)
                  etagManager.clearETag('user:me') // Also clear the 'me' resource key
                  console.log('🔄 ETag cache cleared after Firebase UID migration')

                  setUser(updatedUser)
                } catch (migrationError) {
                  console.error('❌ Failed to migrate Firebase UID:', migrationError)
                  // 移行エラーの場合もログインは続行（後で手動で修正可能）
                  setUser(user)
                }
              } else {
                setUser(user)
              }

              // クロスタブに認証状態変更を通知
              crossTabAuthSync.sendAuthStateChange(true, user.email, 'user_authenticated')
            } catch (error) {
              const apiError = error as { status?: number; message?: string }

              // Handle specific error cases
              if (
                apiError.status === 404 ||
                (apiError.status === 401 && apiError.message === 'User not found')
              ) {
                // User doesn't exist in backend, create them

                // Generate fallback name for both user and space creation
                const fallbackName = firebaseUser.displayName || email.split('@')[0]

                // Create user in backend (same as signup process)
                const user = await createUser({
                  email,
                  fullName: fallbackName,
                })

                // Create default space (same as signup process)
                const space = await createSpace({
                  name: `${fallbackName}'s Space`,
                })

                if (!space?.id) {
                  throw new Error('Failed to create default space during login')
                }

                setUser(user)

                // クロスタブに認証状態変更を通知
                crossTabAuthSync.sendAuthStateChange(true, user.email, 'user_created_with_space')
              } else if (apiError.status === 403) {
                // 403 Forbidden - Security violation
                console.error('Security: Unauthorized access attempt', {
                  email,
                  error: apiError.message,
                })
                setUser(null)
                // Force logout on security violation
                await signOut(auth)
              } else if (apiError.status === 401) {
                // 401 Unauthorized - Token issues (but not "User not found")
                console.error('Authentication token invalid', {
                  email,
                  error: apiError.message,
                })

                // クロスタブでのトークン競合の可能性を考慮して、複数回リトライ
                const maxRetries = 3
                let retrySuccess = false

                for (let retry = 0; retry < maxRetries; retry++) {
                  try {
                    // Firebase認証状態を再確認
                    if (auth.currentUser) {
                      // リトライ前に待機（エクスポネンシャルバックオフ）
                      if (retry > 0) {
                        const backoffTime = Math.min(500 * Math.pow(2, retry - 1), 2000) // 500ms, 1000ms, 2000ms
                        console.log(`Retry ${retry + 1}/${maxRetries}: waiting ${backoffTime}ms`)
                        await new Promise(resolve => setTimeout(resolve, backoffTime))
                      }

                      // Firebaseでは認証されているが、バックエンドで401が発生
                      // トークンリフレッシュを試行
                      const refreshedToken = await auth.currentUser.getIdToken(true)
                      if (refreshedToken) {
                        console.warn(
                          `Token refreshed after 401 error (retry ${retry + 1}/${maxRetries})`
                        )
                        // トークンリフレッシュに成功した場合は、もう一度ユーザー取得を試行
                        const user = await getCurrentUser()
                        setUser(user)

                        // クロスタブに認証状態変更を通知
                        crossTabAuthSync.sendAuthStateChange(
                          true,
                          user.email,
                          'token_refreshed_and_user_retrieved'
                        )
                        retrySuccess = true
                        break
                      }
                    }
                  } catch (retryError) {
                    console.error(
                      `Token refresh failed (retry ${retry + 1}/${maxRetries}):`,
                      retryError
                    )
                    // 最後のリトライでない場合は続行
                    if (retry < maxRetries - 1) {
                      continue
                    }
                  }
                }

                // すべてのリトライが失敗した場合のみログアウト
                if (!retrySuccess) {
                  console.error('All token refresh retries failed, logging out')
                  setUser(null)
                  // トークンキャッシュをクリア
                  clearTokenCache()
                  await signOut(auth)
                }
              } else {
                // Other errors
                console.error('Error getting user data:', apiError)
                throw error
              }
            }
          } else {
            setUser(null)

            // クロスタブに認証状態変更を通知
            crossTabAuthSync.sendAuthStateChange(false, undefined, 'user_logged_out_firebase')
          }
        } catch (error) {
          console.error('Error in auth state change:', error)
          setUser(null)
        } finally {
          setLoading(false)
        }
      }, debounceTime)
    })

    return () => {
      unsubscribe()
      if (authCheckTimeoutRef.current) {
        clearTimeout(authCheckTimeoutRef.current)
      }
    }
  }, [isSigningUp, loading])

  const handleSignOut = async () => {
    try {
      // セキュリティトークンをクリア
      csrfManager.clearToken()
      etagManager.clearAllETags()

      // ユーザーキャッシュをクリア
      if (user?.email) {
        clearUserCache(user.email)
      }

      // トークンキャッシュをクリア
      clearTokenCache()

      await signOut(auth)
      setUser(null)

      // クロスタブにログアウトを通知
      crossTabAuthSync.sendLogout('manual_logout')

      navigate('/login')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const handleLogin = async (email: string, password: string) => {
    try {
      const { user: firebaseUser } = await signInWithEmailAndPassword(auth, email, password)
      if (!firebaseUser.email) throw new Error('No email from Firebase')

      // Check if email is verified
      if (!firebaseUser.emailVerified) {
        // メール未認証の場合は特別な値を返してログイン画面側で処理
        return 'EMAIL_NOT_VERIFIED' // 特別な文字列を返す
      }

      // ✅ SECURITY FIX: Use self-access only
      const user = await getCurrentUser()

      // 🔄 Check and sync email address if different
      if (user.email !== firebaseUser.email) {
        console.log('🔄 Email address mismatch detected, syncing DB with Firebase:', {
          dbEmail: user.email,
          firebaseEmail: firebaseUser.email,
          firebaseUid: firebaseUser.uid,
        })

        try {
          // Firebase UIDを使って、DB側のメールアドレスをFirebase側に合わせる
          const updatedUser = await updateUserEmail(user.email, firebaseUser.email)
          console.log('✅ Email address synchronized successfully')
          setUser(updatedUser)
        } catch (syncError) {
          console.error('❌ Failed to sync email address:', syncError)
          // 同期エラーの場合もログインは続行（後で手動で修正可能）
          setUser(user)
        }
      } else {
        setUser(user)
      }

      if (user.spaces?.[0]?.id) {
        navigate(`/space/${user.spaces[0].id}`)
        return user.spaces[0].id
      } else {
        navigate('/')
        return undefined
      }
    } catch (error) {
      console.error('Error logging in:', error)
      throw error
    }
  }

  const handleSignup = async (email: string, password: string, fullName: string) => {
    try {
      setIsSigningUp(true)

      // Create Firebase user first
      await createUserWithEmailAndPassword(auth, email, password)

      // Create user in backend
      const user = await createUser({
        email,
        fullName,
      })

      // Create default space
      const space = await createSpace({
        name: `${fullName}'s Space`,
      })
      if (!space?.id) {
        throw new Error('Failed to create default space')
      }

      // Send verification email
      await sendVerificationEmail()

      // Set user data but don't trigger navigation here
      setUser(user)

      return space.id
    } catch (error) {
      console.error('Error signing up:', error)
      // Clean up Firebase user if backend creation fails
      try {
        await auth.currentUser?.delete()
      } catch (deleteError) {
        console.error('Error cleaning up Firebase user:', deleteError)
      }
      throw error
    } finally {
      setIsSigningUp(false)
    }
  }

  const handleRefreshUser = async () => {
    try {
      const updatedUser = await getCurrentUser()
      setUser(updatedUser)
    } catch (error) {
      console.error('Error refreshing user:', error)
      // WHY: エラー時は既存のユーザー状態を維持（ログアウトさせない）
    }
  }

  const value = {
    user,
    loading,
    isSigningUp,
    signOut: handleSignOut,
    login: handleLogin,
    signup: handleSignup,
    refreshUser: handleRefreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
